import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useWindowSize } from '@brands/hooks';
import { confirmResetPassword } from '@brands/services/auth/confirmResetPassword';
import { LoginResponse } from '@brands/services/auth/login';
import { yupResolver } from '@hookform/resolvers/yup';
import TextButton from '@mui/material/Button';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import Button from '../../Components/Button/Button';
import PWDRequisite from '../../Components/PasswordValidation/PasswordValidation';
import { customStylesForgotPassword } from '../../Utils/customStyles';
import { displayErrorDetails } from '../../Utils/displayError';
import { hasCapsLetter, hasNumber, hasSpecialChar } from '../../Utils/validation';
import styles from './styles.module.scss';
import leftArrow from './utils/icons/leftArrow.svg';

type ForgotPasswordCredentials = {
  username?: string;
  code: string;
  new_password: string;
  reset_password_hash?: string;
};

interface ForgotPasswordModalStepThreeProps {
  forgotPasswordStep: number;
  setForgotPasswordStep: (num: number) => void;
  credentials: ForgotPasswordCredentials;
  setCredentials: (credentials: ForgotPasswordCredentials) => void;
  resetFlag: boolean;
  triggerLogin?: (response: LoginResponse) => Promise<void>;
}

type ForgotPasswordModalStepThreeSubmitForm = {
  email?: string;
  phoneNumber?: string;
  password?: string;
  confirmPassword?: string;
};

const ForgotPasswordModalStepThree: FC<ForgotPasswordModalStepThreeProps> = ({
  forgotPasswordStep,
  setForgotPasswordStep,
  credentials,
  setCredentials,
  resetFlag,
  triggerLogin,
}) => {
  const navigate = useNavigate();
  const [passwordStr, setPasswordStr] = useState('');
  const [confPasswordStr, setConfPasswordStr] = useState('');
  const isMobile = useWindowSize().width < 992;
  const [checks, setChecks] = useState({
    capsLetterCheck: false,
    numberCheck: false,
    pwdLengthCheck: false,
    specialFlag: false,
  });

  const closeModal = (): void => {
    setForgotPasswordStep(0);
  };

  useEffect(() => {
    if (forgotPasswordStep === 3) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && forgotPasswordStep === 0) {
      document.body.classList.remove('modal-open');
    }
  }, [forgotPasswordStep]);

  const onSubmit = async (data: ForgotPasswordModalStepThreeSubmitForm): Promise<void> => {
    if (!credentials.reset_password_hash && (!credentials.code || !credentials.username)) {
      toast.error('Something went wrong. Please contact support.');
      return;
    }
    try {
      await confirmResetPassword({
        reset_password_hash: credentials.reset_password_hash,
        email: credentials.username,
        verification_code: credentials.code,
        new_password: data.password as string,
      })
        .then(async (res) => {
          if (triggerLogin) {
            await triggerLogin(res as LoginResponse);
          } else {
            setForgotPasswordStep(forgotPasswordStep + 1);
          }
          setCredentials({
            username: '',
            code: '',
            new_password: '',
          });
        })
        .catch((error) => {
          displayErrorDetails(error);
          setCredentials({
            username: credentials.username,
            code: '',
            new_password: '',
          });
          setForgotPasswordStep(0);
        });
    } catch (error: unknown) {
      displayErrorDetails(error);
      setCredentials({
        username: credentials.username,
        code: '',
        new_password: '',
      });
      setForgotPasswordStep(0);
    }
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required()
      .min(8)
      .matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[><?@_+'`~^%&\*\[\]\{\}.!#|\\\"$';,:;=/\(\),\-])(?=.{8,})/),
    confirmPassword: Yup.string()
      .required('Confirm Password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords do not match'),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
    getValues,
    clearErrors,
  } = useForm<ForgotPasswordModalStepThreeSubmitForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    const { password, confirmPassword } = getValues();
    if (password && confirmPassword && password !== confirmPassword) {
      setError('confirmPassword', {
        type: 'manual',
        message: 'Passwords do not match',
      });
    }
    if (password && confirmPassword && password === confirmPassword) {
      clearErrors('confirmPassword');
    }
  }, [getValues('password'), getValues('confirmPassword')]);

  const handleOnChangeConfirm = (e: React.ChangeEvent<HTMLInputElement>): void => {
    errors.confirmPassword = undefined;
    const { value } = e.target;
    setConfPasswordStr(value);
    setValue('confirmPassword', value, { shouldValidate: false });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    errors.confirmPassword = undefined;
    const { value } = e.target;
    setValue('password', value, { shouldValidate: true });
    const capsLetterCheck = hasCapsLetter(value);
    const numberCheck = hasNumber(value);
    const specialFlag = hasSpecialChar(value);
    const pwdLengthCheck = value.length >= 8;
    setChecks({
      capsLetterCheck,
      numberCheck,
      pwdLengthCheck,
      specialFlag,
    });
    setPasswordStr(value);
    if (confPasswordStr !== '') setValue('confirmPassword', confPasswordStr, { shouldValidate: false });
  };

  return (
    <div className={styles.container}>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog hideModals={() => setForgotPasswordStep(0)} />
      <Modal
        isOpen={forgotPasswordStep === 3}
        onRequestClose={closeModal}
        style={customStylesForgotPassword(isMobile, '50%')}
        contentLabel="Modal"
        ariaHideApp={false}
        className={`${styles.wrapper} ${styles.forgotPasswordModal}`}
        shouldCloseOnOverlayClick={false}
      >
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
          <span className={`${styles.formTitle} p-b-26 flex flex-column justify-content-center align-items-center`}>
            <span className={`${styles.formTitleForgot} fs-unmask`}>
              {resetFlag ? 'Change your password' : 'Forgot Password'}
            </span>
          </span>
          <div className={styles.inputWrap}>
            <div className={styles.inputWrapper}>
              <TextField
                {...register('password')}
                label="New password"
                type="password"
                name="password"
                showPasswordToggle
                onChange={handleOnChange}
                autoComplete="new-password"
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
              />
            </div>
          </div>
          <div style={{ marginTop: '16px' }}>
            <PWDRequisite
              capsLetterFlag={checks.capsLetterCheck ? 'valid' : 'invalid'}
              numberFlag={checks.numberCheck ? 'valid' : 'invalid'}
              pwdLengthFlag={checks.pwdLengthCheck ? 'valid' : 'invalid'}
              specialFlag={checks.specialFlag ? 'valid' : 'invalid'}
            />
          </div>
          <div className={styles.inputWrap}>
            <div className={styles.inputWrapper} data-validate="Enter password">
              <TextField
                {...register('confirmPassword')}
                errors={errors.confirmPassword ? errors : {}}
                name="confirmPassword"
                type="password"
                onChange={handleOnChangeConfirm}
                showPasswordToggle
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                label="Confirm new password"
              />
              {errors.confirmPassword && (
                <div className={`${styles.invalidFeedback} invalid-feedback`}>{errors.confirmPassword.message}</div>
              )}
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <div className={styles.buttonWrapper}>
              <div className={styles.buttonBG} />
              <Button
                type="submit"
                className={`${styles.formBtn}`}
                disabled={
                  errors.confirmPassword !== undefined ||
                  errors.password !== undefined ||
                  passwordStr === '' ||
                  confPasswordStr === '' ||
                  passwordStr !== confPasswordStr
                }
              >
                Change Password
              </Button>
            </div>
          </div>
          <div className={`${styles.backArrow}`}>
            <TextButton
              variant="text"
              onClick={() => {
                closeModal();
                navigate('/');
              }}
            >
              <img src={leftArrow} />
              <span>back to sign in</span>
            </TextButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

Modal.setAppElement('#root');

export default ForgotPasswordModalStepThree;
